import {Injectable} from '@angular/core';
import {Story} from "../interfaces/story";
import {StoryCard} from "../story/card/story-card";
import {StoryCardLayout} from "../story/card/story-card-layout";
import {StoryTheme} from "../story/card/story-theme";
import {StoryCardImagePosition} from "../story/card/story-card-image-position";
import {ImageService} from "../atoms/image/image.service";
import {Video} from "../interfaces/video";
import {GetGlobalSearchResults_globalSearch_stories} from "../queries/__generated__/GetGlobalSearchResults";
import {GlobalSearchResult} from "../search-filter/global-search-overlay/global-search-result";
import {Image} from "../interfaces/image";
import {GlobalSearchResultType} from "../enums/global-search-result-type";

// Common shape of the video data in GET_ALL_STORIES, GET_STORY_BY_ID and the storyFields fragment
interface RawStoryVideo {
  title: string | null;
  author: string | null;
  authorUrl: string | null;
  thumbnailUrl: string | null;
  thumbnailHeight: number | null;
  thumbnailWidth: number | null;
  src: string | null;
}

// Common shape of the card data in GET_ALL_STORIES, GET_STORY_BY_ID and the storyFields fragment
// (enum values typed as string so the generated enum types fit as well, images are passed to ImageService untouched)
interface RawStoryCard {
  title: string | null;
  time: string;
  backgroundImage: unknown;
  backgroundVideo: RawStoryVideo | null;
  embedVideo: RawStoryVideo | null;
  lottieAnimation: string | null;
  textHeading: string | null;
  text: string | null;
  image: unknown;
  imagePosition?: string | null;
  highlightPosition: string | null;
  layout: string;
  theme: string;
}

// Common shape of the story data in GET_ALL_STORIES, GET_STORY_BY_ID and the storyFields fragment
// (fields not queried by all of them are optional)
export interface RawStory {
  id?: number | null;
  bicID: number | null;
  title: string | null;
  portrait: string | null;
  color: string | null;
  icon: {
    iconValue: string;
    color: string | null;
  } | null;
  gradient: string | null;
  user: {
    avatar: unknown;
    position: string | null;
  } | null;
  image: unknown;
  cards: RawStoryCard[] | null;
  nextStories: {
    title: string | null;
    description: string | null;
    stories: {
      avatar: unknown;
      title: string | null;
      linkTitle: string | null;
      storyId: string | null;
    }[] | null;
  } | null;
  vrDirectLink: string | null;
  schoolProfile: {
    uuid: string;
  } | null;
  quizDone?: boolean;
  quizLink?: string | null;
  jobName?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class StoryService {

  constructor(private image: ImageService) { }

  mapRawStoryData(rawStoryData: RawStory): Story {
    let parsedStory: Story = {
      id: rawStoryData.id ?? -1,
      bicId: rawStoryData.bicID ?? -1,
      title: rawStoryData.title ?? "",
      portrait: rawStoryData.portrait ?? "",
      color: this.stringToStoryColorEnum(rawStoryData.color),
      icon: rawStoryData.icon as Story["icon"],
      gradient: rawStoryData.gradient as string,
      cards: [],
      user: {
        avatar: this.image.mapRawImageData(rawStoryData.user?.avatar),
        position: rawStoryData.user?.position ?? ""
      },
      nextStories: {
        stories: [],
        title: "",
        description: ""
      },
      vrDirectLink: rawStoryData.vrDirectLink ?? "",
      schoolProfileUuid: rawStoryData.schoolProfile?.uuid ?? "",
      quizDone: rawStoryData.quizDone ?? false
    }

    parsedStory.cards.push({
      backgroundImage: undefined,
      backgroundVideo: undefined,
      embedVideo: undefined,
      image: rawStoryData.image as Image,
      imagePosition: StoryCardImagePosition.TOP,
      layout: StoryCardLayout.COVER,
      lottieAnimation: rawStoryData.icon?.iconValue,
      text: rawStoryData.icon?.color?.toLowerCase(),
      theme: parsedStory.color ?? 0,
      time: "",
      title: rawStoryData.title as string
    });

    (rawStoryData.cards ?? []).forEach((rawCardData) => {
      if(rawCardData.textHeading && rawCardData.textHeading.length) {
        if(rawCardData.highlightPosition === "BEFORE") {
          // console.log("ADD BEFORE");
          parsedStory.cards.push(this.mapRawStoryCardAsHighlight(rawCardData, parsedStory.color));
        }

        parsedStory.cards.push(this.mapRawStoryCard(rawCardData, parsedStory.color));

        if(rawCardData.highlightPosition === "AFTER" || rawCardData.highlightPosition === null) {
          // console.log("ADD AFTER")
          parsedStory.cards.push(this.mapRawStoryCardAsHighlight(rawCardData, parsedStory.color));
        }
      } else {
        parsedStory.cards.push(this.mapRawStoryCard(rawCardData, parsedStory.color));
      }
    });
    // console.log(parsedStory);

    if(rawStoryData.quizLink && rawStoryData.quizLink.length) {
      parsedStory.cards.push({
        backgroundImage: undefined,
        backgroundVideo: undefined,
        embedVideo: undefined,
        image: undefined,
        imagePosition: StoryCardImagePosition.TOP,
        layout: StoryCardLayout.QUIZ,
        lottieAnimation: '/assets/lottie-animations/quiz/BFR-Illustration-2.json',
        text: rawStoryData.quizLink,
        theme: parsedStory.color,
        time: "22:00",
        title: rawStoryData.jobName ?? (rawStoryData.title ?? "")
      });
    }


    if(rawStoryData.nextStories && rawStoryData.nextStories?.stories?.length) {

      rawStoryData.nextStories.stories.forEach((el) => {
        parsedStory.nextStories.stories.push({
          linkTitle: el.title as string,
          title: parsedStory.portrait,
          // backend delivers storyId as string, kept as is
          storyId: el.storyId as unknown as number,
          avatar: this.image.mapRawImageData(el.avatar)
        })
      });
    }

    return parsedStory;

  }

  mapRawStoryCard(rawStoryCardData: RawStoryCard, forceColor: StoryTheme | null = null): StoryCard {
    let parsedStoryCard: StoryCard = {
      title: rawStoryCardData.title ?? "",
      backgroundImage: this.image.mapRawImageData(rawStoryCardData.backgroundImage),
      backgroundVideo: rawStoryCardData.backgroundVideo ? this.mapRawVideoData(rawStoryCardData.backgroundVideo) : undefined,
      embedVideo: rawStoryCardData.embedVideo ? this.mapRawVideoData(rawStoryCardData.embedVideo) : undefined,
      image: this.image.mapRawImageData(rawStoryCardData.image),
      layout: this.stringToCardLayout(rawStoryCardData.layout),
      imagePosition: this.getCardImagePosition(rawStoryCardData.imagePosition),
      text: rawStoryCardData.text ?? "",
      time: rawStoryCardData.time ?? "",
      theme: forceColor !== null ? forceColor : this.getCardTheme(rawStoryCardData.theme),
      lottieAnimation: rawStoryCardData.lottieAnimation ?? "",
    }

    return parsedStoryCard;
  }

  mapRawStoryCardAsHighlight(rawStoryCardData: RawStoryCard, forceColor: StoryTheme | null = null): StoryCard {
    let parsedStoryCard: StoryCard = {
      title: rawStoryCardData.textHeading as string,
      backgroundImage: undefined,
      backgroundVideo: undefined,
      embedVideo: undefined,
      image: undefined,
      layout: StoryCardLayout.HIGHLIGHT,
      imagePosition: undefined,
      text: undefined,
      time: rawStoryCardData.time ?? "",
      theme: forceColor !== null ? forceColor : this.getCardTheme(rawStoryCardData.theme),
      lottieAnimation: undefined,
    }

    return parsedStoryCard;
  }

  getCardTheme(cardTheme: string): StoryTheme {

    switch (cardTheme.toLowerCase()) {
      case "yellow":
        return StoryTheme.YELLOW;
      case "red":
        return StoryTheme.RED;
      case "blue":
        return StoryTheme.PETROL;
      default:
        return StoryTheme.PETROL;
    }
  }

  getCardImagePosition(imagePosition: string | null | undefined): StoryCardImagePosition {
    return StoryCardImagePosition.CENTER;
    switch (imagePosition?.toLowerCase()) {
      case "TOP":
        return StoryCardImagePosition.TOP;
      case "BOTTOM":
        return StoryCardImagePosition.BOTTOM;
      default:
        return StoryCardImagePosition.CENTER;
    }
  }

  mapRawVideoData(rawData: RawStoryVideo): Video { // TODO: if we need this parse somewhere else then create own service (idk is it common interface or not)
    return {
      author: rawData.author ?? "",
      authorUrl: rawData.authorUrl ?? "",
      src: rawData.src ?? "",
      thumbnailHeight: rawData.thumbnailHeight ?? 0,
      thumbnailUrl: rawData.thumbnailUrl ?? "",
      thumbnailWidth: rawData.thumbnailWidth ?? 0,
      title: rawData.title ?? ""
    }
  }

  stringToStoryColorEnum(str: string | null): StoryTheme {
    if(str === "BLACK") return StoryTheme.BLACK;
    if(str === "DARKBLUE") return StoryTheme.DARKBLUE;
    if(str === "DARKGREEN") return StoryTheme.DARKGREEN;
    if(str === "GREY") return StoryTheme.GREY;
    if(str === "LIGHTBLUE") return StoryTheme.LIGHTBLUE;
    if(str === "LIGHTGREEN") return StoryTheme.LIGHTGREEN;
    if(str === "PETROL") return StoryTheme.PETROL;
    if(str === "RED") return StoryTheme.RED;
    if(str === "WHITE") return StoryTheme.WHITE;
    if(str === "YELLOW") return StoryTheme.YELLOW;
    return StoryTheme.YELLOW;
  }

  stringToCardLayout(str: string): StoryCardLayout {
    // return StoryCardLayout.HIGHLIGHT;
    if(str === "VIDEO") return StoryCardLayout.VIDEO;
    if(str === "CONTENT") return StoryCardLayout.CONTENT;
    if(str === "COVER") return StoryCardLayout.HIGHLIGHT;
    if(str === "QUIZGAME") return StoryCardLayout.QUIZGAME;
    return StoryCardLayout.CONTENT;
  }

  mapGlobalSearchResult(rawData: GetGlobalSearchResults_globalSearch_stories): GlobalSearchResult {
    let type: GlobalSearchResultType = GlobalSearchResultType.DAILY_ROUTINE;
    if(rawData.portrait?.toLowerCase() === "schulportrait") type = GlobalSearchResultType.SCHOOL_PORTRAIT;
    if(rawData.portrait?.toLowerCase() === "vr-video") type = GlobalSearchResultType.VR_VIDEO;
    return {
      id: (rawData.id ?? -1).toString(),
      image: rawData.image as unknown as Image,
      quote: "",
      title: rawData.title ?? "",
      type: type,
      isGreen: false
    }
  }

}
